import { useCallback, useEffect, useRef, useState } from "react";
import ContainerCustom from "../../../Components/ContainerCustom";
import Table from "../../../Components/Table/Table";
import TableButton from "../../../Components/Buttons/TableButton";
import StatusColorful from "../../../Components/Table/Components/StatusColorful";
import { isoToJalaliDateTime } from "../../../helper/isoToJalali";
import { API_BASE_URL } from "../../../config/api";
import { observeInfiniteScroll } from "../../../helper/observeInfiniteScroll";

const COUPONS_ENDPOINT =
  `${API_BASE_URL}/api/v0/challenges/admin/coupons/all/`;
const REFRESH_ENDPOINT =
  `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`;
const COUPON_FILTER_QUERY_MAP = [1, 2, 3];
const COUPON_RESPONSE_GROUPS = [
  { key: "active", filterIndex: 0, status: "active" },
  { key: "limit_full", filterIndex: 1, status: "stall" },
  { key: "expired", filterIndex: 2, status: "expired" },
];

function logoutAndRedirect() {
  localStorage.removeItem("access");
  localStorage.removeItem("authCredentials");
  localStorage.removeItem("refresh");
  localStorage.removeItem("role");
  localStorage.removeItem("email");
  window.location.href = "/signin";
}

async function authorizedGet(url) {
  let response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });

  if (response.status === 401) {
    const refreshResponse = await fetch(REFRESH_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    });

    if (!refreshResponse.ok) {
      logoutAndRedirect();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);

    response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access")}`,
      },
    });
  }

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.json();
}

function formatExpiredAt(value) {
  return isoToJalaliDateTime(value, { timeZone: "Asia/Tehran" });
}

function getSelectedBackendFilters(activeFilter) {
  return activeFilter.flatMap((isActive, index) =>
    isActive && COUPON_FILTER_QUERY_MAP[index] !== undefined
      ? [COUPON_FILTER_QUERY_MAP[index]]
      : [],
  );
}

function buildCouponsUrl({ page, activeFilter }) {
  const params = new URLSearchParams();
  const selectedFilters = getSelectedBackendFilters(activeFilter);

  params.set("page", String(page));
  params.set("filter", selectedFilters.join(","));

  return `${COUPONS_ENDPOINT}?${params.toString()}`;
}

function mapCouponRow(coupon, fallbackStatus = "") {
  return {
    uuid: coupon.id ?? "-",
    discountCode: coupon.name ?? "-",
    discountPercent: coupon.discount_percent ?? "-",
    challenge: coupon.is_for_all_challenges
      ? "همه"
      : coupon.challenge_category
        ? `دسته ${coupon.challenge_category}`
        : (coupon.challenge_name ?? "-"),
    user: coupon.user ?? "برای همه",
    expiration: formatExpiredAt(coupon.expired_at),
    status: fallbackStatus,
    detail: "",
  };
}

function mapCouponsResponse(data, activeFilter) {
  return COUPON_RESPONSE_GROUPS.flatMap(({ key, filterIndex, status }) => {
    if (!activeFilter[filterIndex] || !Array.isArray(data?.[key])) {
      return [];
    }

    return data[key].map((coupon) => mapCouponRow(coupon, status));
  });
}

function mergeRows(previousRows, nextRows) {
  const seen = new Set(previousRows.map((row) => row.uuid));
  const uniqueRows = nextRows.filter((row) => {
    if (seen.has(row.uuid)) return false;
    seen.add(row.uuid);
    return true;
  });

  return [...previousRows, ...uniqueRows];
}

export default function CouponList() {
  const [activeFilter, setActiveFilter] = useState([true, true, true]);
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    count: 0,
    pageSize: 0,
    totalPages: 1,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const requestIdRef = useRef(0);
  const loadMoreRef = useRef(null);

  const fetchCouponsData = useCallback(
    async ({ page = 1, replace = false }) => {
      const selectedFilters = getSelectedBackendFilters(activeFilter);

      if (selectedFilters.length === 0) {
        setTableData([]);
        setPagination({
          page: 1,
          count: 0,
          pageSize: 0,
          totalPages: 0,
        });
        setIsLoading(false);
        setIsLoadingMore(false);
        setError("");
        return;
      }

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      setError("");

      if (replace) {
        setIsLoading(true);
        setIsLoadingMore(false);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const data = await authorizedGet(
          buildCouponsUrl({
            page,
            activeFilter,
          }),
        );

        if (!data || requestId !== requestIdRef.current) {
          return;
        }

        const mappedRows = mapCouponsResponse(data, activeFilter);

        setTableData((previousRows) =>
          replace ? mappedRows : mergeRows(previousRows, mappedRows),
        );
        setPagination({
          page: data.page ?? page,
          count: data.count ?? mappedRows.length,
          pageSize: data.page_size ?? mappedRows.length,
          totalPages: data.total_page ?? data.total_pages ?? page,
        });
      } catch (fetchError) {
        if (requestId !== requestIdRef.current) {
          return;
        }

        console.error("Error fetching coupon data:", fetchError);
        setError("خطا در دریافت لیست کدهای تخفیف");

        if (replace) {
          setTableData([]);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [activeFilter],
  );

  useEffect(() => {
    fetchCouponsData({
      page: 1,
      replace: true,
    });
  }, [fetchCouponsData]);

  useEffect(() => {
    const target = loadMoreRef.current;

    if (!target) {
      return;
    }

    return observeInfiniteScroll(target, () => {
        if (
          isLoading ||
          isLoadingMore ||
          pagination.page >= pagination.totalPages ||
          pagination.totalPages === 0
        ) {
          return;
        }

        fetchCouponsData({
          page: pagination.page + 1,
          replace: false,
        });
      });
  }, [
    fetchCouponsData,
    isLoading,
    isLoadingMore,
    pagination.page,
    pagination.totalPages,
  ]);

  return (
    <ContainerCustom
      header="لیست کدهای تخفیف"
      headSVG="archive"
      filter={false}
      sort={false}
      search={false}
      colorfulFilterOptions={[
        {
          SVGName: "tick",
          text: "فعال",
          className: "w-28",
          color: "green",
        },
        {
          SVGName: "stall",
          text: "ظرفیت تکمیل",
          className: "w-28",
          color: "yellow",
        },
        {
          SVGName: "failed",
          text: "منقضی شده",
          className: "w-28",
          color: "red",
        },
      ]}
      activeFilter={activeFilter}
      setActiveFilter={setActiveFilter}
    >
      <div className="flex flex-col">
        <Table
          columns={[
            { key: "uuid", label: "کد یکتا" },
            { key: "discountCode", label: "کد تخفیف" },
            { key: "discountPercent", label: "درصد تخفیف" },
            { key: "challenge", label: "مناسب برای چالش" },
            { key: "user", label: "کاربر" },
            { key: "expiration", label: "تاریخ انقضا" },
            {
              key: "status",
              label: "وضعیت",
              render: (value) =>
                value === "active" ? (
                  <StatusColorful
                    SVGName="tick"
                    text="فعال"
                    className="w-28"
                    color="green"
                  />
                ) : value === "stall" ? (
                  <StatusColorful
                    SVGName="stall"
                    text="ظرفیت تکمیل"
                    className="w-28"
                    color="yellow"
                  />
                ) : value === "expired" ? (
                  <StatusColorful
                    SVGName="failed"
                    text="منقضی شده"
                    className="w-28"
                    color="red"
                  />
                ) : (
                  ""
                ),
            },
            {
              key: "detail",
              label: "جزئیات",
              render: (value, row) => (
                <TableButton
                  text="مشاهده"
                  href=""
                  onClick={() => {
                    window.location.href = `/coupons/${row.uuid}/coupon-info`;
                  }}
                  className="!py-1 !px-2 !rounded-md bg-zi-bg1-light dark:bg-zi-bg1-dark text-zi-text-light dark:text-zi-text-dark hocus:ring-1 hocus:ring-inset hocus:ring-zi-border-dark"
                />
              ),
            },
          ]}
          data={tableData}
          loading={isLoading}
          loadingMore={isLoadingMore}
          error={error}
          onRetry={() => fetchCouponsData(1, false)}
          showEmptyState
          emptyMessage="نتیجه‌ای مطابق فیلترها یافت نشد"
          bottomRef={loadMoreRef}
        />
      </div>
    </ContainerCustom>
  );
}
